import { useMemo, useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Button, H5, InputCheckbox } from "@sellar-io/components";
import {
  FulfillmentTypeGetResponseType,
  DeliveryMethodContainerRuleUnitType,
} from "@sellar-io/api-v2/types";
import { useDeliveryMethodContainerRulesContainerTypes } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesContainerTypes";
import { useDeliveryMethodContainerRulesCreate } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesCreate";
import { useDeliveryMethodContainerRulesDelete } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesDelete";
import { ItemTypeImage, SpinnerLegacy } from "../../../components";
import { TableInfinite } from "../../../components/table/TableInfinite";

export const DeliveryMethodContainerRulesDialog = ({
  active,
  setActive,
  deliveryMethod,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  deliveryMethod?: FulfillmentTypeGetResponseType;
}) => {
  const availableContainersQuery =
    useDeliveryMethodContainerRulesContainerTypes();

  const availableContainers = useMemo(
    () => availableContainersQuery.data ?? [],
    [availableContainersQuery.data],
  );

  const excludedContainersFromRules = useMemo(() => {
    return new Set(
      deliveryMethod?.DeliveryMethodContainerRules?.map(
        (rule) => `${rule.containerType}-${rule.volume}-${rule.unit}`,
      ) ?? [],
    );
  }, [deliveryMethod]);
  const [excludedContainers, setExcludedContainers] = useState(
    excludedContainersFromRules,
  );
  const [previousExcludedContainersFromRules, setPreviousRules] = useState(
    excludedContainersFromRules,
  );
  if (excludedContainersFromRules !== previousExcludedContainersFromRules) {
    setPreviousRules(excludedContainersFromRules);
    setExcludedContainers(excludedContainersFromRules);
  }

  const createRuleMutation = useDeliveryMethodContainerRulesCreate();
  const deleteRuleMutation = useDeliveryMethodContainerRulesDelete();

  const onSave = async () => {
    if (!deliveryMethod) return;

    const existingRules = deliveryMethod.DeliveryMethodContainerRules;

    const existingRulesMap = new Map(
      existingRules.map((r) => [
        `${r.containerType}-${r.volume}-${r.unit}`,
        r.id,
      ]),
    );

    const containersToExclude = availableContainers.filter((c) =>
      excludedContainers.has(`${c.containerType}-${c.volume}-${c.displayUnit}`),
    );

    // Rules to create: in excludedContainers but not in existingRules
    const toCreate = containersToExclude.filter(
      (c) =>
        !existingRulesMap.has(
          `${c.containerType}-${c.volume}-${c.displayUnit}`,
        ),
    );

    // Rules to delete: in existingRules but not in excludedContainers
    const toDelete = existingRules.filter(
      (r) =>
        !excludedContainers.has(`${r.containerType}-${r.volume}-${r.unit}`),
    );

    const promises: Promise<{ id: number }>[] = [];

    toCreate.forEach((c) => {
      promises.push(
        createRuleMutation.mutateAsync({
          containerType: c.containerType,
          unit: c.displayUnit as DeliveryMethodContainerRuleUnitType,
          volume: c.volume,
          FulfillmentTypeId: deliveryMethod.id,
          SupplierId: null,
          systemDefinedType: null,
        }),
      );
    });

    toDelete.forEach((r) => {
      promises.push(deleteRuleMutation.mutateAsync(r.id));
    });

    await Promise.all(promises);
    setActive(false);
  };

  const isSaving = createRuleMutation.isPending || deleteRuleMutation.isPending;

  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="Edit container availability"
      />
      <DialogContent className="flex flex-col gap-4">
        <div>
          <H5>Edit container rules</H5>
          <p className="text-sm text-zinc-500">
            Select which of your listed containers can be shipped via this
            delivery method
          </p>
        </div>

        {availableContainersQuery.isLoading ? (
          <div className="flex justify-center p-4">
            <SpinnerLegacy />
          </div>
        ) : (
          <TableInfinite
            roundedTop={true}
            gridClasses="grid-cols-[1fr_100px]"
            columns={[
              {
                header: "Containers",
                cell: ({ row }) => (
                  <div className="flex items-center gap-2">
                    <ItemTypeImage
                      product={{
                        containerType: row.original.containerType,
                        type: row.original.containerType,
                      }}
                      size="small"
                      withoutBackground
                    />
                    <p>
                      {`${row.original.volume}${row.original.displayUnit} ${row.original.displayName}`}
                    </p>
                  </div>
                ),
              },
              {
                header: "Available?",
                cell: ({ row }) => {
                  const key = `${row.original.containerType}-${row.original.volume}-${row.original.displayUnit}`;
                  return (
                    <div className="flex justify-center">
                      <InputCheckbox
                        required={false}
                        checked={!excludedContainers.has(key)}
                        onChange={() => {
                          const newExcluded = new Set(excludedContainers);
                          if (newExcluded.has(key)) {
                            newExcluded.delete(key);
                          } else {
                            newExcluded.add(key);
                          }
                          setExcludedContainers(newExcluded);
                        }}
                      />
                    </div>
                  );
                },
              },
            ]}
            data={availableContainers}
            infiniteQuery={availableContainersQuery}
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button color="transparent" onClick={() => setActive(false)}>
          Cancel
        </Button>
        <Button onClick={onSave} loading={isSaving}>
          Save
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
